import { Injectable, inject, signal } from '@angular/core';
import { AccessMode } from '../core/constants/app.constants';
import { AppConstants } from '../core/constants/app.constants';
import { HttpClient } from '@angular/common/http';
import { SignalRService } from './signalr.service';
import { CookieUtil } from '../core/utils/cookie.util';
import { Observable, of } from 'rxjs';
import { delay, tap, map, catchError, switchMap } from 'rxjs/operators';
import { v4 as uuidv4 } from 'uuid';
import { EndpointsService } from './endpoints.service';
import { ApiResponse } from '../core/models/api-response.model';
import { CreateSessionRequest, SessionSearchRequest, SessionResponse } from '../core/models/session.model';

export interface SessionData {
  token: string;
  tireSpecSessionGuid: string;
  visitorGuid: string;
  websiteGuid: string;
  accessMode?: AccessMode;
}

@Injectable({ providedIn: 'root' })
export class SessionService {
  private readonly VISITOR_COOKIE_NAME = AppConstants.Cookies.VisitorGuid;

  private currentSessionSignal = signal<SessionData | null>(null);
  public readonly currentSession = this.currentSessionSignal.asReadonly();

  private http = inject(HttpClient);
  private signalRService = inject(SignalRService);
  private endpointsService = inject(EndpointsService);

  initializeSession(websiteGuid: string): Observable<SessionData> {
    const existingSession = this.currentSession();
    if (existingSession) {
      return of(existingSession);
    }
    let visitorGuid: string | null = CookieUtil.get(this.VISITOR_COOKIE_NAME);
    const guidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
    if (!visitorGuid || !guidRegex.test(visitorGuid)) {
      try {
        visitorGuid = uuidv4();
      } catch (e) {
        visitorGuid = '00000000-0000-0000-0000-000000000000';
      }
      CookieUtil.set(this.VISITOR_COOKIE_NAME, visitorGuid, 365);
    }

    const request: CreateSessionRequest = {
      WebsiteGuid: websiteGuid,
      VisitorGuid: visitorGuid
    };

    return this.http.post<ApiResponse<SessionResponse>>(this.endpointsService.createSession, request).pipe(
      switchMap(async (response: ApiResponse<SessionResponse>) => {
        const res = response.data;
        const sessionData: SessionData = {
          token: res.jsonWebToken,
          tireSpecSessionGuid: res.tireSpecSessionGuid,
          visitorGuid: visitorGuid as string,
          websiteGuid: websiteGuid,
        };
        this.currentSessionSignal.set(sessionData);
        return sessionData;
      })
    );
  }

  validateSession(tireSpecSessionGuid: string): Observable<SessionData> {
  return this.http.get<ApiResponse<SessionResponse>>(`${this.endpointsService.validateSession}/${tireSpecSessionGuid}`).pipe(
      switchMap(async (response: ApiResponse<SessionResponse>) => {
        const res = response.data;
        const sessionData: SessionData = {
          token: res.jsonWebToken,
          tireSpecSessionGuid: tireSpecSessionGuid,
          visitorGuid: res.visitorGuid,
          websiteGuid: res.websiteGuid || '',
        };
        CookieUtil.set(this.VISITOR_COOKIE_NAME, sessionData.visitorGuid, 7);
        this.currentSessionSignal.set(sessionData);
        return sessionData;
      })
    );
  }

  private deviceInstanceId: string | null = null;

  getDeviceInstanceId(): string {
    if (!this.deviceInstanceId) {
      if (typeof window !== 'undefined' && window.sessionStorage) {
        let storedId = window.sessionStorage.getItem('TireSpecDeviceInstanceId');
        if (!storedId) {
          storedId = uuidv4();
          window.sessionStorage.setItem('TireSpecDeviceInstanceId', storedId);
        }
        this.deviceInstanceId = storedId;
      } else {
        this.deviceInstanceId = uuidv4();
      }
    }
    return this.deviceInstanceId;
  }

  setAccessMode(mode: AccessMode): void {
    this.currentSessionSignal.update(s => s ? { ...s, accessMode: mode } : s);
  }

  getAccessMode(): AccessMode | undefined {
    return this.currentSession()?.accessMode;
  }

  endSessionFromPc(): void {
    this.clearSession();
  }

  clearSession(): void {
    this.signalRService.stopConnection();
    this.signalRService.resetState();
    this.currentSessionSignal.set(null);
  }
}


